import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, X } from "lucide-react";
import { api, imgUrl } from "../../lib/api";

export const CodeSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const box = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setRes(null); return undefined; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q: term } })
        .then((r) => setRes(Array.isArray(r.data) ? r.data : []))
        .catch(() => setRes([]))
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) return undefined;
    const out = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", out);
    return () => document.removeEventListener("mousedown", out);
  }, [open]);

  const go = (r) => {
    setOpen(false);
    setQ("");
    navigate(`/c/${r.slug}?page=${r.page}&photo=${encodeURIComponent(r.id)}`);
  };

  return (
    <div ref={box} className="relative">
      <button onClick={() => setOpen((v) => !v)} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#240620]/25 text-[#240620] transition-colors duration-300 hover:bg-[#240620] hover:text-[#FFF8EF]" aria-label="Search by design code" data-testid="code-search-btn">
        {open ? <X size={18} /> : <Search size={18} />}
      </button>
      {open && (
        <div data-lenis-prevent className="fixed left-3 right-3 top-[84px] z-50 rounded-2xl border border-[#D4AF37]/30 bg-[#1d051a] p-3 shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-14 sm:w-[380px]" data-testid="code-search-panel">
          <div className="flex items-center gap-2 rounded-xl bg-[#330B2E] px-3">
            <Search size={16} className="text-[#D4AF37]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder='Design code or name, e.g. "GL044", "P032"' className="h-11 flex-1 bg-transparent text-sm text-[#FFF8EF] placeholder:text-[#E8D8C8]/60 focus:outline-none" data-testid="code-search-input" />
            {busy && <Loader2 size={16} className="animate-spin text-[#F3E5AB]" />}
          </div>
          {res && (
            <ul className="mt-2 max-h-80 space-y-1 overflow-y-auto" data-testid="code-search-results">
              {res.length === 0 && <li className="px-2 py-3 text-sm text-[#E8D8C8]" data-testid="code-search-empty">No design found for "{q}".</li>}
              {res.map((r) => (
                <li key={r.id}>
                  <button onClick={() => go(r)} className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-[#330B2E]" data-testid={`code-search-result-${r.id}`}>
                    <img src={imgUrl(r.url)} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-[#FFF8EF]">{r.title}</span>
                      <span className="text-xs text-[#D4AF37]">{r.category}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
